/**
 * Catálogo de Quests — fuente de verdad única.
 *
 * Cada quest se define a mano acá. Los NPCs la ofrecen vía `questIds` en su
 * template (`shared/data/npcs.ts`). Los tipos y helpers puros viven en
 * `shared/types/quest.ts`. Ver docs/QUESTS.md.
 *
 * Convención: IDs internos en inglés, display strings (nameEs/descEs) en español.
 */

import type { QuestDefinition } from '../types/quest';

export const QUEST_DEFINITIONS: Record<string, QuestDefinition> = {
  // Quest de prueba (MVP): matar 5 mobs + traer 1 espada → XP + oro.
  // giver = npc_quest_1 (Capitán Aldric, NPC dedicado a quests). Ids reales para testear.
  quest_intro_hunt: {
    id: 'quest_intro_hunt',
    nameEs: 'Amenaza en las afueras',
    descriptionEs:
      'Una manada de lobos asola las afueras, liderada por un señor orco. Acabá con ellos y ' +
      'traeme una espada como prueba.',
    giverNpcId: 'npc_quest_1',
    objectives: [
      { type: 'kill', npcId: 'hostile_wolf_gray', count: 2, descEs: 'Matar Lobos Grises' },
      { type: 'kill', npcId: 'hostile_wolf_golden', count: 2, descEs: 'Matar Lobos Dorados' },
      { type: 'kill', npcId: 'hostile_wolf_bloody', count: 1, descEs: 'Matar un Lobo Sangriento' },
      { type: 'kill', npcId: 'hostile_orc_lord', count: 1, descEs: 'Derrotar a Lord Orco' },
      { type: 'collect', itemId: 'weapon_sword_001', count: 1, descEs: 'Conseguir una espada' },
    ],
    rewards: {
      xp: 600,
      gold: 150,
    },
  },

  // Quest de tipo kill_kingdom (matar N criaturas de un Reino, sin importar el
  // template concreto). Testeable: los lobos son del Reino 'animal'. Repetible.
  // Ver docs/CREATURE_KINGDOMS.md.
  quest_cull_animals: {
    id: 'quest_cull_animals',
    nameEs: 'Control de fauna',
    descriptionEs:
      'Las bestias salvajes se multiplican sin control en las afueras. No me importa cuáles: ' +
      'reducí su número. Cazá cinco animales y volvé.',
    giverNpcId: 'npc_quest_1',
    repeatable: true,
    objectives: [
      { type: 'kill_kingdom', kingdom: 'animal', count: 5, descEs: 'Cazar animales' },
    ],
    rewards: {
      xp: 300,
      gold: 80,
    },
  },
};

/** Definición de quest por id, o null si no existe. */
export function getQuestDefinition(id: string): QuestDefinition | null {
  return QUEST_DEFINITIONS[id] ?? null;
}

/** Todas las quests ofrecidas por un NPC template (matchea `giverNpcId`). */
export function getQuestsGivenBy(npcId: string): QuestDefinition[] {
  return Object.values(QUEST_DEFINITIONS).filter((q) => q.giverNpcId === npcId);
}

/** Todas las quests que se entregan en un NPC template (turnIn = turnInNpcId ?? giverNpcId). */
export function getQuestsTurnedInAt(npcId: string): QuestDefinition[] {
  return Object.values(QUEST_DEFINITIONS).filter(
    (q) => (q.turnInNpcId ?? q.giverNpcId) === npcId
  );
}
